import { pushRoute } from '../../../lib/router.js';
import { omit } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { normalize } from 'normalizr';
import {
  deleteStakeholder,
  finishUpdateStakeholderDraft,
  selectStakeholder,
  selectStakeholderDraft,
  selectStakeholderDraftId,
  updateStakeholderDraft,
} from '../../../modules/stakeholders.js';
import {
  retrieveEmployees,
  selectRetrieveEmployeesState,
} from '../../../modules/employees.js';
import {
  navigateToStepCommunicators,
  navigateToStepInformation,
  onCreateUpdateCancel,
  onParticipantsUploadChange,
  onReset,
  stakeholdersUrl,
  stakeholderUrl,
} from './utils/index.js';
import { selectProject } from '../../../modules/projects.js';
import StepGroupParticipantsCsvForm from '../../../components/projects/Stakeholders/StakeholderForms/StepGroupParticipantsCsvForm.jsx';
import StepGroupParticipantsManuallyForm from '../../../components/projects/Stakeholders/StakeholderForms/StepGroupParticipantsManuallyForm.jsx';
import {
  getOrgSlug,
  isDateTodayOrAfter,
  mapFinalFormErrors,
} from '../../../lib/utils.js';
import { useFetchFile } from '../../../hooks/useFetchFile.js';
import { retrieveOrgGroups } from '../../../modules/orgGroups.js';
import MultistepCombinedHeader from '../../../components/headers/FormHeaders/MultistepCombinedHeader.jsx';
import StakeholderFormHeader from '../../../components/headers/PageHeaders/Project/Stakeholders/StakeholderFormHeader.jsx';
import { deleteGroupMsg, deleteUngroupedEmployeeMsg } from '../utils/index.js';
import ManageGroupModal from '../../../components/manage-group-modal/ManageGroupModal.jsx';
import ManageParticipantsModal from '../../../components/manage-participants-modal/ManageParticipantsModal.jsx';
import { formParticipantSchema } from '../../../lib/schema.js';
import GroupFormContainer from '../../organizations/GroupFormContainer.jsx';

const mapErrors = mapFinalFormErrors('Failed to save stakeholder');

const mapStateToProps = (state, ownProps) => ({
  stakeholderId: ownProps.stakeholderId,
  editing: !!ownProps.stakeholderId,
  projectId: ownProps.projectId,
  project: selectProject(ownProps.projectId)(state),
  draftId: selectStakeholderDraftId(state),
  retrieveEmployeesSuccess: selectRetrieveEmployeesState(state).success,
  stakeholder: omit(selectStakeholder(state, ownProps), [
    'id',
    'influencePosition',
    'interestPosition',
  ]),
  stakeholderDraft: selectStakeholderDraft(selectStakeholderDraftId(state))(
    state
  ),
  apiUrl: state.config.apiUrl,
});

const mapDispatchToProps = {
  deleteStakeholder,
  retrieveEmployees,
  retrieveOrgGroups,
  updateStakeholderDraft,
  finishUpdateStakeholderDraft,
};

function StepParticipantsFormContainer({
  projectId,
  draftId,
  editing,
  deleteStakeholder,
  stakeholder,
  stakeholderId,
  retrieveOrgGroups,
  stakeholderDraft,
  project,
  updateStakeholderDraft,
  finishUpdateStakeholderDraft,
  apiUrl,
}) {
  const searchQuery = {
    objectType: 'stakeholder',
    objectId: stakeholderId,
    active: !stakeholderId,
  };
  const [uploadMethod, setUploadMethod] = useState('manually');
  const [replaceMethod, setReplaceMethod] = useState('add');
  const [showManageGroupModal, setShowManageGroupModal] = useState(false);
  const [showManageParticipantsModal, setShowManageParticipantsModal] =
    useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);

  const [editableGroupId, setEditableGroupId] = useState(null);

  const [entities, setEntities] = useState({
    groups: {},
    ungroupedEmployees: [],
  });

  const { downloadBlob } = useFetchFile();

  const formRef = useRef(null);

  const onHideGroupModal = () => setShowGroupModal(false);
  const onShowGroupModal = () => setShowGroupModal(true);
  const onHideManageParticipantsModal = () =>
    setShowManageParticipantsModal(false);
  const onShowManageParticipantsModal = (id) =>
    setShowManageParticipantsModal(true);
  const onHideManageGroupModal = () => setShowManageGroupModal(false);
  const onShowManageGroupModal = (id) => {
    setEditableGroupId(id);
    setShowManageGroupModal(true);
  };

  const participantsEditAllowed = () =>
    editing && project?.endDate ? isDateTodayOrAfter(project.endDate) : true;

  const goBack = () =>
    navigateToStepInformation(editing, projectId, stakeholderId, getOrgSlug());
  const goNext = () =>
    navigateToStepCommunicators(
      editing,
      projectId,
      stakeholderId,
      getOrgSlug()
    );

  const updateEntities = (payload) => {
    setEntities({
      ...entities,
      ...payload,
    });
  };

  const reAssignUngrouped = ({ id, name, employee_ids }) => {
    const commonIds = entities.ungroupedEmployees.filter((id) =>
      employee_ids.includes(id)
    );
    const restUngrouped = entities.ungroupedEmployees.filter(
      (id) => !commonIds.includes(id)
    );
    updateEntities({
      ungroupedEmployees: restUngrouped,
      groups: {
        ...entities.groups,
        [String(id)]: {
          id,
          name,
          employee_ids: commonIds,
        },
      },
    });
  };

  const onSelectGroup = (group) => {
    if (group.id in entities.groups) {
      if (!window.confirm(deleteGroupMsg)) return;

      const { [String(group.id)]: _, ...newObject } = entities.groups;
      updateEntities({ groups: newObject });
    } else {
      updateEntities({
        groups: {
          ...entities.groups,
          [String(group.id)]: {
            id: group.id,
            name: group.name,
            employee_ids: [],
          },
        },
      });
      reAssignUngrouped({ ...group, employee_ids: group.employeeIds });
    }
  };

  const onAddGroupSubmitSuccess = ({ entities, result }) => {
    retrieveOrgGroups(searchQuery).then(() =>
      onSelectGroup(entities.orgGroups[result])
    );
  };

  const onSelectUngroupedEmployee = (e) => {
    const isActive = entities.ungroupedEmployees.includes(e.id);
    if (isActive) {
      if (!window.confirm(deleteUngroupedEmployeeMsg)) return;
      updateEntities({
        ungroupedEmployees: entities.ungroupedEmployees.filter(
          (i) => i !== e.id
        ),
      });
    } else {
      updateEntities({
        ungroupedEmployees: [...entities.ungroupedEmployees, e.id],
      });
    }
  };

  const onSubmitManageGroup = (group) =>
    updateEntities({
      groups: { ...entities.groups, [editableGroupId]: group },
    });

  const onSubmitManageUngrouped = (employees) =>
    updateEntities({ ungroupedEmployees: employees });

  const onDownloadCsv = () => {
    downloadBlob(
      apiUrl,
      `stakeholders/${stakeholderId}/group_structure_csv`,
      null,
      'current-group-participation CSV',
      'csv'
    );
  };

  const onSubmitCsv = (values) => {
    const { finish } = values;
    const formData = new FormData(formRef.current);

    if (formData.has('stakeholder[csv_groups_file]')) {
      formData.append('stakeholder[groups_merge]', replaceMethod === 'add');
      formData.append(
        'stakeholder[manually_groups]',
        !!uploadMethod === 'manually'
      );

      const submitPromise = finish
        ? finishUpdateStakeholderDraft(draftId, formData, true)
        : updateStakeholderDraft(draftId, formData, true);

      return submitPromise
        .then(({ result }) =>
          finish
            ? pushRoute(stakeholderUrl, { projectId, stakeholderId: result })
            : goNext()
        )
        .catch(mapErrors);
    }

    finish ? pushRoute(stakeholderUrl, { projectId, stakeholderId }) : goNext();
  };

  const onSubmitManually = (values) => {
    const { finish } = values;

    const resource = {
      groups_data: [
        ...Object.values(entities.groups),
        { id: null, employee_ids: entities.ungroupedEmployees },
      ],
    };

    const submitPromise = finish
      ? finishUpdateStakeholderDraft(draftId, resource, false)
      : updateStakeholderDraft(draftId, resource, false);

    return submitPromise
      .then(({ result }) =>
        finish
          ? pushRoute(stakeholderUrl, { projectId, stakeholderId: result })
          : goNext()
      )
      .catch(mapErrors);
  };

  const onCancel = () =>
    onCreateUpdateCancel(draftId, deleteStakeholder, projectId);

  const onDelete = () => {
    if (
      !window.confirm(
        'Are you sure you want to delete this stakeholder profile?'
      )
    )
      return;
    deleteStakeholder(stakeholderId).then(() =>
      pushRoute(stakeholdersUrl, { projectId })
    );
  };

  useEffect(() => {
    !draftId && onReset(editing, projectId, stakeholderId, getOrgSlug());
    retrieveOrgGroups(searchQuery);
  }, []);

  useEffect(() => {
    if (stakeholderDraft) {
      if (stakeholderDraft.groups_data) {
        const filteredGroups = stakeholderDraft.groups_data.filter(
          (g) => g.id !== null
        );
        const ungrouped = stakeholderDraft.groups_data.find(
          (g) => g.id === null
        );
        const normalizedGroups =
          normalize(filteredGroups, [formParticipantSchema]).entities.groups ||
          {};
        const ungroupedEmployees = ungrouped ? ungrouped.employee_ids : [];
        updateEntities({
          groups: normalizedGroups,
          ungroupedEmployees,
        });
      }
      stakeholderDraft.csv_groups_name && setUploadMethod('csv');
    }
  }, []);

  return (
    <React.Fragment>
      <MultistepCombinedHeader
        onDelete={onDelete}
        editing={editing}
        subject="Stakeholder Profile"
        typeStep="People"
      >
        {project && (editing ? Object.keys(stakeholder).length : true) ? (
          <StakeholderFormHeader
            project={project}
            editing={editing}
            stakeholder={stakeholder}
            stakeholderId={stakeholderId}
          />
        ) : null}
      </MultistepCombinedHeader>
      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              {stakeholderDraft ? (
                <Row>
                  <Col sm={10} smOffset={1}>
                    {uploadMethod === 'csv' ? (
                      <Form
                        component={StepGroupParticipantsCsvForm}
                        onSubmit={onSubmitCsv}
                        onDownloadCsv={onDownloadCsv}
                        onBack={goBack}
                        onCancel={onCancel}
                        uploadMethod={uploadMethod}
                        setUploadMethod={(method) =>
                          onParticipantsUploadChange(method, setUploadMethod)
                        }
                        replaceMethod={replaceMethod}
                        setReplaceMethod={setReplaceMethod}
                        selectedCsvName={stakeholderDraft.csv_groups_name || ''}
                        stakeholderDraft={stakeholderDraft}
                        editing={editing}
                        formRef={formRef}
                        participantsEditAllowed={participantsEditAllowed()}
                      />
                    ) : null}

                    {uploadMethod === 'manually' ? (
                      <Form
                        onSubmit={onSubmitManually}
                        editing={editing}
                        entities={entities}
                        uploadMethod={uploadMethod}
                        setUploadMethod={(method) =>
                          onParticipantsUploadChange(method, setUploadMethod)
                        }
                        replaceMethod={replaceMethod}
                        setReplaceMethod={setReplaceMethod}
                        stakeholderDraft={stakeholderDraft}
                        onSelectGroup={onSelectGroup}
                        onShowManageGroupModal={onShowManageGroupModal}
                        onSelectUngroupedEmployee={onSelectUngroupedEmployee}
                        onShowManageParticipantsModal={
                          onShowManageParticipantsModal
                        }
                        onShowGroupModal={onShowGroupModal}
                        ungroupedSearchParams={{
                          excludingGroupIds: Object.keys(entities.groups),
                        }}
                        onBack={goBack}
                        onCancel={onCancel}
                        participantsEditAllowed={participantsEditAllowed()}
                        component={StepGroupParticipantsManuallyForm}
                      />
                    ) : null}
                  </Col>
                </Row>
              ) : null}
            </div>
          </Col>
        </Row>
      </Grid>

      <ManageGroupModal
        hideSelectAll
        showFormModal={showManageGroupModal}
        onHideFormModal={onHideManageGroupModal}
        group={entities.groups[editableGroupId]}
        onSubmit={onSubmitManageGroup}
        additionalSearchParams={{
          objectType: 'stakeholder',
          objectId: stakeholderId,
        }}
      />

      <ManageParticipantsModal
        employeeIds={entities.ungroupedEmployees}
        showFormModal={showManageParticipantsModal}
        onHideFormModal={onHideManageParticipantsModal}
        additionalSearchParams={{
          excludingGroupIds: Object.keys(entities.groups),
          objectType: 'stakeholder',
          objectId: stakeholderId,
        }}
        onSubmit={onSubmitManageUngrouped}
        title="Impacted People"
      />

      <GroupFormContainer
        showFormModal={showGroupModal}
        onHideFormModal={onHideGroupModal}
        onSubmitSuccess={onAddGroupSubmitSuccess}
      />
    </React.Fragment>
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StepParticipantsFormContainer);
